import React, { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  HStack,
  VStack,
} from "../../components/utils";
import { Select } from "../../components/shared/Select";
import SearchInput from "../../components/shared/SearchInput";
import {
  useGetGrantLetterTemplates,
  useBulkAssignGrantLetterTemplate,
} from "../../queries/siteSettings";
import { queryClient } from "../../queries";
import { Grant } from "../../types/Grant";
import { GrantLetterTemplate } from "../../types/SiteSettings";

interface AssignGrantLetterTemplatesModalProps {
  open: boolean;
  onClose: () => void;
  grants: Grant[];
}

interface GrantRowData {
  grantIdentifier: string;
  optionHolderId: string;
  optionHolderName: string;
  planName: string;
  existingTemplateName: string;
  assignedTemplateId: string;
  assignedTemplateName: string;
}

function getTemplatesList(raw: unknown): GrantLetterTemplate[] {
  if (Array.isArray(raw)) return raw as GrantLetterTemplate[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.grantLetterTemplates))
      return obj.grantLetterTemplates as GrantLetterTemplate[];
    if (Array.isArray(obj.data)) return obj.data as GrantLetterTemplate[];
  }
  return [];
}

export default function AssignGrantLetterTemplatesModal({
  open,
  onClose,
  grants,
}: AssignGrantLetterTemplatesModalProps) {
  const navigate = useNavigate();
  const gridApiRef = useRef<GridApi | null>(null);
  const [searchText, setSearchText] = useState("");
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedRows, setSelectedRows] = useState<GrantRowData[]>([]);
  const [assignmentMap, setAssignmentMap] = useState<
    Record<string, { templateId: string; templateName: string }>
  >({});

  const { data: rawTemplates, isLoading: templatesLoading } =
    useGetGrantLetterTemplates();
  const templates = getTemplatesList(rawTemplates).filter(
    (t) => t.entityType === "GRANT"
  );

  const { mutate: bulkAssign, isLoading: assigning } =
    useBulkAssignGrantLetterTemplate();

  const rowData: GrantRowData[] = useMemo(
    () =>
      grants.map((g) => {
        const mapping = assignmentMap[g.optionHolderId];
        return {
          grantIdentifier: g.grantIdentifier || g.optionHolderId,
          optionHolderId: g.optionHolderId,
          optionHolderName: g.optionHolderName || "",
          planName: g.planName || "",
          existingTemplateName: g.grantLetterTemplateName || "",
          assignedTemplateId: mapping?.templateId || "",
          assignedTemplateName: mapping?.templateName || "",
        };
      }),
    [grants, assignmentMap]
  );

  const columnDefs: ColDef<GrantRowData>[] = useMemo(
    () => [
      {
        headerName: "",
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 50,
        maxWidth: 50,
        pinned: "left",
        suppressMenu: true,
        sortable: true,
        filter: false,
        resizable: false,
      },
      {
        headerName: "Grant Identifier",
        field: "grantIdentifier",
        flex: 1,
        minWidth: 160,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Employee",
        field: "optionHolderName",
        flex: 1,
        minWidth: 150,
        sortable: true,
        filter: false,
      },
      {
        headerName: "Plan",
        field: "planName",
        flex: 1,
        minWidth: 120,
        sortable: true,
        filter: true,
      },
      {
        headerName: "Assigned Grant Letter Template",
        flex: 2,
        minWidth: 250,
        sortable: true,
        filter: false,
        cellRenderer: (params: { data: GrantRowData }) => {
          const row = params.data;
          if (row.assignedTemplateName) {
            return (
              <HStack className="items-center gap-2 h-full">
                <span className="text-sm text-gray-800">
                  {row.assignedTemplateName}
                </span>
                <button
                  type="button"
                  className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove assignment"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveAssignment(row.optionHolderId);
                  }}
                >
                  <Icon icon="ph:x" width={14} />
                </button>
              </HStack>
            );
          }
          if (row.existingTemplateName) {
            return (
              <span className="text-sm text-gray-400 italic">
                {row.existingTemplateName} (current)
              </span>
            );
          }
          return (
            <span className="text-sm text-gray-300 italic">Not assigned</span>
          );
        },
      },
    ],
    [assignmentMap]
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      menuTabs: [],
      getMainMenuItems: () => [],
      suppressMovable: true,
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
  }, []);

  const onSelectionChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const selected = gridApiRef.current.getSelectedRows() as GrantRowData[];
    setSelectedRows(selected);
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
      if (gridApiRef.current) {
        gridApiRef.current.setQuickFilter(e.target.value);
      }
    },
    []
  );

  function handleRemoveAssignment(optionHolderId: string) {
    setAssignmentMap((prev) => {
      const next = { ...prev };
      delete next[optionHolderId];
      return next;
    });
  }

  function handleOpenTemplateSelector() {
    if (selectedRows.length === 0) {
      toast("Please select at least one grant from the table", {
        type: "warning",
        autoClose: 2000,
      });
      return;
    }
    setSelectedTemplateId("");
    setTemplateSelectorOpen(true);
  }

  function handleConfirmTemplateAssignment() {
    if (!selectedTemplateId) {
      toast("Please select a template", { type: "error", autoClose: 2000 });
      return;
    }
    const template = templates.find(
      (t) => (t.templateId ?? t.planName ?? "") === selectedTemplateId
    );
    if (!template) return;

    const templateName =
      template.templateName ?? template.planName ?? selectedTemplateId;

    setAssignmentMap((prev) => {
      const next = { ...prev };
      selectedRows.forEach((row) => {
        next[row.optionHolderId] = {
          templateId: selectedTemplateId,
          templateName,
        };
      });
      return next;
    });
    setTemplateSelectorOpen(false);
    setSelectedTemplateId("");
    if (gridApiRef.current) {
      gridApiRef.current.deselectAll();
    }
  }

  function handleApplyAll() {
    const entries = Object.entries(assignmentMap);
    if (entries.length === 0) {
      toast("No template assignments to apply", {
        type: "warning",
        autoClose: 2000,
      });
      return;
    }

    const grantIds: string[] = [];
    const grantIdToTemplateIdMap: Record<string, string> = {};
    entries.forEach(([optionHolderId, { templateId }]) => {
      const grant = grants.find((g) => g.optionHolderId === optionHolderId);
      const grantIdentifier = grant?.grantIdentifier || optionHolderId;
      grantIds.push(grantIdentifier);
      grantIdToTemplateIdMap[grantIdentifier] = templateId;
    });

    bulkAssign(
      { grantIds, grantIdToTemplateIdMap },
      {
        onSuccess: (data) => {
          const assigned = data?.assigned ?? grantIds.length;
          if (assigned > 0) {
            toast(`${assigned} template(s) assigned successfully`, {
              type: "success",
              autoClose: 3000,
            });
          }
          if (data?.errors?.length > 0) {
            toast(
              `Errors: ${data.errors
                .map(
                  (e: { grantId: string; reason: string }) =>
                    `${e.grantId}: ${e.reason}`
                )
                .join(", ")}`,
              { type: "error", autoClose: 5000 }
            );
          }
          queryClient.invalidateQueries("allGrants");
          queryClient.refetchQueries("allGrants");
          onClose();
        },
        onError: (err: unknown) => {
          const e = err as Record<string, unknown>;
          const response = e?.response as Record<string, unknown> | undefined;
          const errData = response?.data as Record<string, unknown> | undefined;
          const message =
            typeof errData?.errors === "string"
              ? errData.errors
              : "Failed to assign templates";
          toast(message, { type: "error", autoClose: 3000 });
        },
      }
    );
  }

  const assignedCount = Object.keys(assignmentMap).length;
  const unassignedCount = grants.length - assignedCount;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          height: "80vh",
          overflowY: "auto",
        },
      }}
    >
      <VStack className="h-full bg-white overflow-visible">
        {/* Header */}
        <div className="py-7 border-b border-gray-200 text-lg">
          <HStack className="justify-between items-center px-8">
            <VStack>
              <h2 className="font-semibold text-gray-dark">
                Assign Grant Letter Templates
              </h2>
              <p className="text-xs font-medium text-gray-500">
                {grants.length} grant{grants.length !== 1 ? "s" : ""} selected
                &nbsp;&middot;&nbsp; Ensure required templates exist before
                proceeding. Click{" "}
                <span
                  className="text-red-500 underline cursor-pointer"
                  onClick={() =>
                    navigate("/settings/siteSettings", {
                      state: { activeSetting: "Grant Template Settings" },
                    })
                  }
                >
                  here
                </span>
                &nbsp;to add more templates.
              </p>
            </VStack>
            <span onClick={onClose} className="cursor-pointer">
              X
            </span>
          </HStack>
        </div>

        {/* Toolbar */}
        <HStack className="px-6 py-6 justify-between items-center border-b border-gray-100">
          <div className="w-64">
            <SearchInput
              placeholder="Search grants..."
              value={searchText}
              onChange={handleSearchChange}
            />
          </div>
          <HStack className="items-center gap-3">
            <ButtonPrimary1
              onClick={handleOpenTemplateSelector}
              disabled={selectedRows.length === 0}
              className="text-xs2"
            >
              Assign to Selected
            </ButtonPrimary1>
          </HStack>
        </HStack>

        {/* AG Grid Table */}
        <div className="px-6 py-3 ag-theme-material">
          <div style={{ width: "100%" }}>
            <AgGridReact<GrantRowData>
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              sideBar={false}
              domLayout="autoHeight"
              rowSelection="multiple"
              suppressRowClickSelection={true}
              onGridReady={onGridReady}
              onSelectionChanged={onSelectionChanged}
              animateRows={true}
              rowHeight={44}
              headerHeight={44}
              overlayNoRowsTemplate="<span class='text-sm text-gray-400'>No grants found</span>"
              suppressCellFocus={true}
              getRowId={(params) => params.data.optionHolderId}
            />
          </div>
        </div>

        <div className="p-8 text-xs text-gray-500 mt-2 min-w-[1020px]">
          <p className="mb-1">
            * This action only assigns grant letter templates. Letter generation
            is not performed here.{" "}
          </p>
          <p className="mb-1">
            * To generate grant letters using the assigned templates, please use
            &quot;Generate Grant Letters&quot; option from the bulk actions menu
            on the Grant Details table.{" "}
          </p>
        </div>

        {/* Footer */}
        <HStack className="px-6 py-4 border-t border-gray-200 justify-between gap-3 items-center bg-white">
          <HStack className="items-center gap-2">
            <ButtonPrimary1 onClick={onClose}>Cancel</ButtonPrimary1>
          </HStack>
          <HStack className="gap-3">
            <ButtonPrimary
              onClick={handleApplyAll}
              loading={assigning}
              disabled={assignedCount === 0}
            >
              Assign
            </ButtonPrimary>
          </HStack>
        </HStack>
      </VStack>

      {/* Template Selection Dialog */}
      <Dialog
        open={templateSelectorOpen}
        onClose={() => setTemplateSelectorOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="border-b border-gray-200 pb-3 p-6">
          <h3 className="flex justify-between text-base font-semibold text-gray-dark ">
            Select Template
            <span
              onClick={() => setTemplateSelectorOpen(false)}
              className="cursor-pointer"
            >
              X
            </span>
          </h3>
        </div>
        <VStack className="p-6 bg-white gap-5">
          <p className="text-sm text-gray-500">
            Assign a template to{" "}
            <span className="font-medium text-gray-700">
              {selectedRows.length}
            </span>{" "}
            selected grant{selectedRows.length !== 1 ? "s" : ""}.
          </p>

          {templatesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Icon
                icon="lucide:loader-2"
                className="animate-spin text-gray-400"
                width={28}
              />
            </div>
          ) : templates.length === 0 ? (
            <VStack className="items-center justify-center py-8 text-gray-400 gap-2">
              <Icon icon="ph:file-dashed" width={40} />
              <span className="text-sm">No templates available</span>
              <span
                className="text-xs text-blue-600 underline cursor-pointer"
                onClick={() =>
                  navigate("/settings/siteSettings", {
                    state: { activeSetting: "Grant Template Settings" },
                  })
                }
              >
                Upload from Grant Letter Settings
              </span>
            </VStack>
          ) : (
            <Select
              options={templates}
              placeholder="Select a template..."
              value={selectedTemplateId}
              valueGetter={(t) => t.templateId ?? t.planName ?? ""}
              textGetter={(t) => t.templateName ?? t.planName ?? ""}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="w-full"
            />
          )}

          <HStack className="justify-between gap-3 mt-2">
            <ButtonPrimary1 onClick={() => setTemplateSelectorOpen(false)}>
              Cancel
            </ButtonPrimary1>
            <ButtonPrimary
              onClick={handleConfirmTemplateAssignment}
              disabled={!selectedTemplateId}
            >
              Confirm
            </ButtonPrimary>
          </HStack>
        </VStack>
      </Dialog>
    </Dialog>
  );
}
